/**
 * Hand-written mirrors of the API's C# records. Enums serialize as their PascalCase C# names and
 * properties as camelCase, so the string unions below are the wire values verbatim.
 */

import type { ApiValidationFailure } from './http';

// ---------------------------------------------------------------------------
// Domain enums (src/Domain/Common/Enums.cs)
// ---------------------------------------------------------------------------

export const fileKinds = [
  'BisDocument',
  'IsdocDocument',
  'Attachment',
  'InboundXml',
  'InboundPdf',
  'InboundJson',
  'InboundAttachment',
] as const;
export type FileKind = (typeof fileKinds)[number];

/** Kinds that may be sent to the gate as the document of a send operation. */
export const documentFileKinds: readonly FileKind[] = ['BisDocument', 'IsdocDocument'];

export const fileSources = ['Upload', 'Generated', 'FlowisInbound'] as const;
export type FileSource = (typeof fileSources)[number];

export const sendOperationStatuses = ['Queued', 'Submitted', 'Duplicate', 'Rejected', 'Failed'] as const;
export type SendOperationStatus = (typeof sendOperationStatuses)[number];

export const mappingKinds = [
  'Unit',
  'PaymentMeans',
  'DocumentType',
  'TaxCategory',
  'TaxExemptionReason',
  'AllowanceReason',
  'ChargeReason',
  'Country',
  'Currency',
  'TaxPointDateCode',
  'PartyIdScheme',
  'StandardItemScheme',
  'ItemClassScheme',
  'AttachmentMimeType',
] as const;
export type MappingKind = (typeof mappingKinds)[number];

export const codelistVersionStatuses = ['Draft', 'Active', 'Superseded'] as const;
export type CodelistVersionStatus = (typeof codelistVersionStatuses)[number];

export const codelistEntryStatuses = ['Active', 'New', 'Deprecated'] as const;
export type CodelistEntryStatus = (typeof codelistEntryStatuses)[number];

export const fileReferenceRoles = ['Document', 'SentPayload', 'Attachment'] as const;
export type FileReferenceRole = (typeof fileReferenceRoles)[number];

// ---------------------------------------------------------------------------
// Paging (libs/AlgoBridge.Common.EntityFrameworkCore/Paging)
// ---------------------------------------------------------------------------

export interface PagedMetadata {
  /** One-based. The data grid counts from zero — always convert through src/api/paging.ts. */
  currentPage: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface PagedResponse<T> {
  items: T[];
  pagedMetadata: PagedMetadata;
}

export type SortDirection = 'asc' | 'desc';

export interface PageableSortableRequest {
  /** One-based. */
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: SortDirection;
}

// ---------------------------------------------------------------------------
// Caller context (Application/Models/Tenants/TenantModels.cs)
// ---------------------------------------------------------------------------

export interface CallerTenant {
  tenantGid: string;
  name: string;
}

/**
 * Who the API decided the caller is. Everything the console has to know before it can offer a write:
 * which tenants exist to file it under, whether to ask at all, and whether administration is reachable.
 */
export interface CallerContext {
  /** Ordered by name. Empty means nobody has assigned a tenant yet — normal, not a failure. */
  tenants: CallerTenant[];
  /** Reaches every tenant and may administer the registry; granted by a directory role. */
  isAdministrator: boolean;
  /** The signed-in person's Entra object id; null for a machine caller. */
  userObjectId: string | null;
  /**
   * Whether the caller reaches the full tenant-facing surface (the `user` or `admin` role) rather
   * than only the integration contract a bare `registration` role grants — submitting documents
   * and following them up. The console narrows its navigation to match.
   */
  hasFullAccess: boolean;
}

// ---------------------------------------------------------------------------
// Accounts (Application/Models/Accounts/AccountModels.cs)
// ---------------------------------------------------------------------------

export interface FlowisAccount {
  accountGid: string;
  name: string;
  /** Peppol participant identifier in scheme:id form, e.g. 0245:2120147656. */
  participantId: string | null;
  enabled: boolean;
  tenantGid: string;
  tenantName: string;
}

// ---------------------------------------------------------------------------
// Files (Application/Models/Files/*.cs)
// ---------------------------------------------------------------------------

export interface StoredFile {
  fileGid: string;
  tenantGid: string;
  tenantName: string;
  kind: FileKind;
  source: FileSource;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  sha256: string | null;
  deduplicated: boolean;
}

/**
 * What storing an invoice document produced. `warnings` only ever carries warnings — anything the
 * rules treat as an error refuses the document, and then there is no result at all.
 */
export interface UploadDocumentResult {
  file: StoredFile;
  warnings: ApiValidationFailure[];
}

export interface StoredFileListItem {
  fileGid: string;
  tenantGid: string;
  tenantName: string;
  kind: FileKind;
  source: FileSource;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  sha256: string | null;
  createdAt: string;
  inUse: boolean;
  operationRefCount: number;
  receivedRefCount: number;
}

export interface FileOperationRef {
  operationGid: string;
  role: FileReferenceRole;
  status: SendOperationStatus;
}

export interface StoredFileDetail {
  file: StoredFile;
  createdAt: string;
  lastModifiedAt: string | null;
  storageProvider: string;
  operations: FileOperationRef[];
  receivedDocumentGids: string[];
}

export interface FileListFilter extends PageableSortableRequest {
  kinds?: FileKind[];
  sources?: FileSource[];
  inUse?: boolean;
  search?: string;
}

/** Sort keys the file listing accepts; anything else falls back to newest first. */
export const fileSortKeys = ['fileName', 'sizeBytes', 'kind', 'source', 'createdAt', 'tenantName'] as const;
export type FileSortKey = (typeof fileSortKeys)[number];

// ---------------------------------------------------------------------------
// Operations (Application/Models/Operations/OperationModels.cs)
// ---------------------------------------------------------------------------

export interface SendOperationListItem {
  operationGid: string;
  tenantGid: string;
  tenantName: string;
  status: SendOperationStatus;
  accountGid: string;
  accountName: string;
  participantId: string | null;
  externalDocumentId: string | null;
  documentFileGid: string;
  documentFileName: string;
  flowisDocumentId: string | null;
  flowisInvoiceId: string | null;
  flowisStatus: string | null;
  errorCode: string | null;
  submittedAt: string | null;
  lastCheckedAt: string | null;
}

export interface SendOperationError {
  code: string;
  detail: string;
  attr: string | null;
  line: number | null;
}

export interface OperationAttachment {
  fileGid: string;
  fileName: string;
}

export interface SendOperation {
  operationGid: string;
  tenantGid: string;
  tenantName: string;
  status: SendOperationStatus;
  duplicate: boolean;
  accountGid: string;
  participantId: string | null;
  /** The caller's own identifier for the document, when they gave one. */
  externalDocumentId: string | null;
  documentFileGid: string;
  sentPayloadFileGid: string | null;
  flowisDocumentId: string | null;
  flowisInvoiceId: string | null;
  flowisStatus: string | null;
  submittedAt: string | null;
  lastCheckedAt: string | null;
  errors: SendOperationError[];
  attachments: OperationAttachment[];
}

/**
 * What a one-call submission answered: the operation it created (or the original one, when
 * `operation.duplicate` says the identifier was seen before), what was stored, and every validation
 * finding — on a 200 these are warnings only, a refusal answers 422 with the findings in the error.
 */
export interface SubmitResult {
  operation: SendOperation | null;
  document: StoredFile | null;
  attachments: StoredFile[];
  validation: ApiValidationFailure[];
}

/**
 * The sending account is not in here: it is the participant identifier in the route. Only the files
 * and the caller's own identifier for the document travel in the body.
 */
export interface SendToFlowisRequest {
  documentFileGid: string;
  attachmentFileGids: string[];
  /** The caller's own identifier; repeating it answers with the first operation, not a second send. */
  externalDocumentId?: string;
}

export interface OperationListFilter extends PageableSortableRequest {
  statuses?: SendOperationStatus[];
  /** The sending account's Peppol participant identifier, in scheme:code form. */
  participantId?: string;
  externalDocumentId?: string;
  submittedFrom?: string;
  submittedTo?: string;
  /**
   * Only the operations the caller themselves filed. A bare `registration` submitter is held to
   * this server-side whether they ask or not.
   */
  mine?: boolean;
}

/** Sort keys the operation listing accepts. */
export const operationSortKeys = [
  'submittedAt',
  'status',
  'flowisStatus',
  'lastCheckedAt',
  'tenantName',
] as const;
export type OperationSortKey = (typeof operationSortKeys)[number];

// ---------------------------------------------------------------------------
// Communication log (Application/Models/Diagnostics/CommunicationLogModels.cs)
// ---------------------------------------------------------------------------

export const communicationChannels = ['SubmitQueue', 'StatusQueue'] as const;
export type CommunicationChannel = (typeof communicationChannels)[number];

export const communicationDirections = ['Inbound', 'Outbound'] as const;
export type CommunicationDirection = (typeof communicationDirections)[number];

export const communicationOutcomes = [
  'Accepted',
  'Retried',
  'Rejected',
  'Published',
  'Failed',
  'Dropped',
] as const;
export type CommunicationOutcome = (typeof communicationOutcomes)[number];

export interface CommunicationLogListItem {
  entryGid: string;
  recordedAt: string;
  channel: CommunicationChannel;
  direction: CommunicationDirection;
  outcome: CommunicationOutcome;
  tenantGid: string | null;
  tenantName: string | null;
  queueName: string | null;
  messageId: string | null;
  subject: string | null;
  externalDocumentId: string | null;
  operationGid: string | null;
  senderParticipantId: string | null;
  receiverParticipantId: string | null;
  deliveryCount: number | null;
  reasonCode: string | null;
  message: string;
  exceptionType: string | null;
}

/** One entry in full. The exception message and stack are filled for administrators only. */
export interface CommunicationLogEntry extends CommunicationLogListItem {
  blobLocator: string | null;
  bodyBytes: number | null;
  detail: string | null;
  exceptionMessage: string | null;
  exceptionStackTrace: string | null;
}

export interface CommunicationLogFilter extends PageableSortableRequest {
  /** Matched against message id, document identifier, participants and reason; a GUID also matches the operation. */
  search?: string;
  channels?: CommunicationChannel[];
  outcomes?: CommunicationOutcome[];
  recordedFrom?: string;
  recordedTo?: string;
}

/** Sort keys the communication log listing accepts. */
export const communicationLogSortKeys = [
  'recordedAt',
  'channel',
  'outcome',
  'tenantName',
  'messageId',
  'reasonCode',
] as const;
export type CommunicationLogSortKey = (typeof communicationLogSortKeys)[number];

/**
 * Flowis statuses a document does not move out of (FlowisSentDocumentStatus.Terminal). Anything else,
 * including a status Flowis adds later, still changes — which is what the server keeps polling and
 * what these views keep refetching for.
 */
export const terminalFlowisStatuses: readonly string[] = [
  'accepted',
  'rejected',
  'not_valid',
  'failed_delivery',
];

/** What this API instance last saw of Flowis (Application/Models/Flowis/FlowisAvailability.cs). */
export interface FlowisAvailability {
  reachable: boolean;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  lastErrorCode: string | null;
  downSince: string | null;
}

/** Whether an operation is still waiting on something: our own delivery, or Flowis'. */
export function isOperationSettled(operation: {
  status: SendOperationStatus;
  flowisDocumentId: string | null;
  flowisStatus: string | null;
}): boolean {
  if (operation.status === 'Queued') return false;

  // Nothing at the gate to wait for — it never took the document, or never will.
  if (!operation.flowisDocumentId) return true;

  return operation.flowisStatus !== null && terminalFlowisStatuses.includes(operation.flowisStatus);
}

// ---------------------------------------------------------------------------
// Flowis passthrough (Application/Models/Flowis/*.cs)
// ---------------------------------------------------------------------------

export interface FlowisPagedResponse<T> {
  /** Read defensively: the gate has violated its own schema before, and a missing total must not cost the page. */
  count: number | null;
  offset: number | null;
  limit: number | null;
  /**
   * Absolute URLs on the gate's own host. Read as has-next / has-previous only — this app never
   * fetches them, because they carry neither our bearer token nor our account header.
   */
  next: string | null;
  previous: string | null;
  results: T[];
}

/** Largest page the gate accepts; a bigger limit is rejected with 400. */
export const flowisMaxLimit = 100;
/** Page size chosen by this console, not the gate's default. */
export const flowisDefaultLimit = 25;

export type FlowisSelfbillFilter = 'Without' | 'Only' | 'With';

export interface FlowisDocumentQuery {
  offset?: number;
  limit?: number;
  status?: string;
  /** Date-only, `yyyy-MM-dd`. Bounds are inclusive and apply to the issue date. */
  fromDate?: string;
  toDate?: string;
  downloaded?: boolean;
  selfbill?: FlowisSelfbillFilter;
}

export const flowisSentStatuses = [
  'draft',
  'new',
  'ready_to_send',
  'not_valid',
  'sent',
  'accepted',
  'rejected',
  'failed_delivery',
] as const;

export const flowisReceivedStatuses = ['new', 'accepted', 'rejected', 'not_valid', 'completed'] as const;

/**
 * Every field is nullable: the gate has answered null where its schema promised a value (a credit
 * note's due date), so the API reads all of them defensively and passes through what actually came.
 */
export interface FlowisDocumentSummary {
  id: string | null;
  invoiceId: string | null;
  documentType: string | null;
  issueDate: string | null;
  documentCurrencyCode: string | null;
  /** Optional in Peppol BIS — null for documents without one (a credit note, typically). */
  dueDate: string | null;
  taxationDate: string | null;
  sellerRegistrationName: string | null;
  sellerCompanyTaxId: string | null;
  sellerCompanyVatId: string | null;
  buyerRegistrationName: string | null;
  buyerCompanyTaxId: string | null;
  buyerCompanyVatId: string | null;
  totalTaxExclusiveAmount: number | null;
  totalTaxAmount: number | null;
  totalVatIncluded: number | null;
  /** Kept open: the gate may add states and an unknown one must not break the page. */
  status: string | null;
  /** Undocumented MLS (message level status) field the gate sends; meaning unknown so far. */
  mlsStatus: string | null;
  downloaded: boolean | null;
  createdAt: string | null;
  isSelfBilling: boolean | null;
  errorCode: string | null;
}

export interface FlowisSentDocument extends FlowisDocumentSummary {
  /** Self-authorizing storage URL, valid for 24 hours — the one link opened as a plain anchor. */
  xmlFileUrl: string | null;
  xmlFileUrlExpiresAt: string | null;
  sourceDocumentUrl: string | null;
  sourceDocumentUrlExpiresAt: string | null;
  generatedPdfUrl: string | null;
}

export interface FlowisReceivedDocument extends FlowisDocumentSummary {
  xmlFileUrl: string | null;
  xmlFileUrlExpiresAt: string | null;
  generatedPdfUrl: string | null;
}

export interface FlowisPeppolParticipant {
  name: string | null;
  participantId: string;
  countryCode: string | null;
  registrationDate: string | null;
  documentTypes: string[];
}

// ---------------------------------------------------------------------------
// Inbound (Application/Models/Inbound/InboundModels.cs)
// ---------------------------------------------------------------------------

export interface InboundItem {
  /** Null when the gate offered an item without an id — shown, but nothing can fetch it. */
  flowisDocumentId: string | null;
  invoiceId: string | null;
  documentType: string | null;
  issueDate: string | null;
  sellerName: string | null;
  totalVatIncluded: number | null;
  status: string | null;
  alreadyTracked: boolean;
}

/** What the participant named in the route is holding at the gate. */
export interface InboundCheckResult {
  pendingCount: number;
  items: InboundItem[];
}

export interface InboundDownloadOptions {
  includePdf?: boolean;
  markDownloaded?: boolean;
}

export interface InboundDownloadResult {
  receivedDocumentGid: string;
  xmlFileGid: string;
  pdfFileGid: string | null;
  jsonFileGid: string | null;
  attachmentFileGids: string[];
  parseError: string | null;
  alreadyDownloaded: boolean;
}

/** An inbound document as this service holds it, with every file it produced. */
export interface InboundDocument {
  receivedDocumentGid: string;
  tenantGid: string;
  flowisDocumentId: string;
  invoiceId: string | null;
  documentType: string | null;
  issueDate: string | null;
  currencyCode: string | null;
  sellerName: string | null;
  buyerName: string | null;
  totalVatIncluded: number | null;
  flowisStatus: string | null;
  xmlFileGid: string | null;
  pdfFileGid: string | null;
  jsonFileGid: string | null;
  attachmentFileGids: string[];
  parseError: string | null;
  downloadedAt: string | null;
  acknowledgedAt: string | null;
  changedAt: string;
}

export interface InboundAckResult {
  receivedDocumentGid: string;
  acknowledgedAt: string;
  alreadyAcknowledged: boolean;
}

// ---------------------------------------------------------------------------
// Mappings (Application/Models/Mappings/MappingModels.cs)
// ---------------------------------------------------------------------------

/** One standard code a mapping may translate into. The description is English from the API. */
export interface StandardCode {
  code: string;
  description: string;
}

export interface TenantMapping {
  mappingGid: string;
  tenantGid: string;
  tenantName: string;
  type: MappingKind;
  sourceValue: string;
  targetValue: string;
  secondaryValue: string | null;
  description: string | null;
  enabled: boolean;
  /** Whether the target is a code the active code list of this kind accepts; null while no version is active. */
  inActiveCodelist: boolean | null;
}

export interface UpsertTenantMappingRequest {
  tenantGid?: string;
  type: MappingKind;
  sourceValue: string;
  targetValue: string;
  secondaryValue: string | null;
  description: string | null;
  enabled: boolean;
}

/** One mapping as it travels in a file: no gid, no tenant — addressed by kind and source value. */
export interface MappingEntry {
  type: MappingKind;
  sourceValue: string;
  targetValue: string;
  secondaryValue: string | null;
  description: string | null;
  enabled: boolean;
}

/** The export answer and the import body share this shape, so a file round-trips unchanged. */
export interface MappingsDocument {
  mappings: MappingEntry[];
}

export const mappingImportModes = ['Merge', 'Replace'] as const;
/** Merge creates or updates and keeps the rest; Replace makes the file the whole table of each kind it contains. */
export type MappingImportMode = (typeof mappingImportModes)[number];

export interface ImportMappingsRequest {
  mappings: MappingEntry[];
  mode: MappingImportMode;
}

export interface ImportMappingsResult {
  created: number;
  updated: number;
  unchanged: number;
  deleted: number;
}

// ---------------------------------------------------------------------------
// Code lists (Application/Models/Codelists/CodelistModels.cs)
// ---------------------------------------------------------------------------

export interface CodelistVersion {
  versionGid: string;
  kind: MappingKind;
  versionLabel: string;
  status: CodelistVersionStatus;
  sourceNote: string | null;
  activatedAt: string | null;
  createdAt: string;
  entryCount: number;
}

export interface CodelistEntry {
  code: string;
  name: string;
  description: string | null;
  status: CodelistEntryStatus;
}

export interface CodelistVersionDetail {
  version: CodelistVersion;
  entries: CodelistEntry[];
}

/** One entry as it is imported; the status is computed by the server. */
export interface CodelistEntryInput {
  code: string;
  name: string;
  description?: string | null;
}

export interface ImportCodelistRequest {
  kind: MappingKind;
  versionLabel: string;
  sourceNote: string | null;
  entries: CodelistEntryInput[];
}

export interface ReplaceCodelistEntriesRequest {
  entries: CodelistEntryInput[];
}

/** An enabled mapping of the acting tenant whose target a version does not accept. */
export interface AffectedMapping {
  mappingGid: string;
  /** The registry is shared, so an affected mapping may belong to any tenant in scope. */
  tenantGid: string;
  tenantName: string;
  sourceValue: string;
  targetValue: string;
}

/** What activating a draft would change, against the version active right now. */
export interface CodelistDiff {
  added: CodelistEntry[];
  deprecated: CodelistEntry[];
  unchangedCount: number;
  affectedMappings: AffectedMapping[];
}

export interface ActivateCodelistResult {
  version: CodelistVersion;
  brokenMappingCount: number;
}

/** The tenant's enabled mappings the active version of one kind no longer accepts. */
export interface BrokenMappings {
  kind: MappingKind;
  activeVersionLabel: string;
  mappings: AffectedMapping[];
}

/** The active version of one kind as the mapping dialog reads it: deprecated entries left out. */
export interface ActiveCodelist {
  kind: MappingKind;
  versionLabel: string;
  entries: ActiveCodelistEntry[];
}

export interface ActiveCodelistEntry {
  code: string;
  name: string;
  description: string | null;
}

// ---------------------------------------------------------------------------
// Administration (Application/Models/Admin/AdminModels.cs)
// ---------------------------------------------------------------------------

export interface TenantSummary {
  tenantGid: string;
  name: string;
  /** Machine code, e.g. `sa-svk-d2` — the value the caller's `tenant.{code}` directory role carries. */
  code: string;
  enabled: boolean;
  testMode: boolean;
  accountCount: number;
}

/**
 * Administration's view of an account also exposes the identifier Flowis itself issued, the
 * identifiers a sender may name it by, and which tenant holds it. A null `tenantGid` means the
 * account is registered but placed with nobody: it waits in the pool and cannot send.
 */
export interface AdminFlowisAccount {
  accountGid: string;
  accountId: string;
  name: string;
  enabled: boolean;
  /** IČO. */
  registrationNumber: string | null;
  /** DIČ. */
  taxId: string | null;
  /** IČ DPH, or a country-prefixed DIČ. */
  vatId: string | null;
  participantId: string | null;
  countryCode: string | null;
  tenantGid: string | null;
  tenantName: string | null;
  /** Whether the account holds its own API client pair; the pair itself is never returned. */
  hasOwnCredential: boolean;
}

/**
 * An account as the gate holds it, marked with the local registration pointing at it. A null
 * `accountGid` is one nobody has registered here yet — the set worth registering.
 */
export interface RemoteFlowisAccount {
  /** Null when the gate listed an account without its identifier — shown, but not registrable. */
  accountId: string | null;
  name: string | null;
  registrationNumber: string | null;
  taxId: string | null;
  vatId: string | null;
  participantId: string | null;
  countryCode: string | null;
  documentMode: string | null;
  status: string | null;
  accountGid: string | null;
  tenantGid: string | null;
  tenantName: string | null;
}

/**
 * Who may act for the tenant is not in here — that is the directory's answer, read off the
 * `tenant.{code}` roles a caller's token carries. The container and queue names are the EFFECTIVE
 * values: the override where one is set, the code-derived fallback otherwise.
 */
export interface TenantDetail {
  tenantGid: string;
  name: string;
  code: string;
  containerName: string;
  queueInName: string;
  queueOutName: string;
  useQueues: boolean;
  /** Flowis is never called: sends are accepted and answered as Flowis would, so the exchange around the bridge can be tested. */
  testMode: boolean;
  enabled: boolean;
  schemaState: 'Ready' | 'Missing' | 'Outdated';
  accounts: AdminFlowisAccount[];
}

/** One live connectivity check: the effective name probed, and what the dependency said on failure. */
export interface TenantProbe {
  target: string;
  ok: boolean;
  detail: string | null;
}

/**
 * Live checks of one tenant's integration points. The probes are null when no Service Bus namespace
 * is configured for the deployment; `consumerEnabled` and `useQueues` say whether a reachable queue
 * is actually being listened to.
 */
/** One of the tenant's Flowis accounts, asked through its own credential whether the gate answers. */
export interface FlowisAccountProbe {
  accountGid: string;
  name: string;
  participantId: string | null;
  enabled: boolean;
  ok: boolean;
  detail: string | null;
}

export interface TenantHealth {
  serviceBusConfigured: boolean;
  consumerEnabled: boolean;
  useQueues: boolean;
  queueIn: TenantProbe | null;
  queueOut: TenantProbe | null;
  container: TenantProbe | null;
  /** The bridge's own file store, written with a marker and cleaned up; deployment-wide, but on every tenant's card. */
  fileStore: TenantProbe;
  flowisAccounts: FlowisAccountProbe[];
}

/**
 * `code` is the tenant's machine identity (lowercase, digits, single dashes) — what the caller's
 * `tenant.{code}` directory role carries, the stem the queue and container names fall back to
 * (`algobridge-{code}-in/-out`, container `algobridge-{code}`), and what the schema name is derived from.
 * The overrides are optional; `useQueues` stays false until the tenant's queues actually exist.
 */
export interface CreateTenantRequest {
  name: string;
  code: string;
  enabled: boolean;
  containerName?: string | null;
  queueInName?: string | null;
  queueOutName?: string | null;
  useQueues: boolean;
  testMode?: boolean;
}

/** Applied wholesale — read the tenant first and send back what should stay. */
export type UpdateTenantRequest = CreateTenantRequest;

/** `Create` provisions the tenant's Azure resources, `Check` only verifies they exist. */
export type ProvisioningScriptKind = 'Create' | 'Check';

/**
 * Built from the form's current values — deliberately not from a stored tenant, so the script can
 * be generated before (or without) saving and reflects exactly what the administrator sees.
 */
export interface TenantProvisioningScriptRequest {
  name: string;
  code: string;
  kind: ProvisioningScriptKind;
  containerName?: string | null;
  queueInName?: string | null;
  queueOutName?: string | null;
  useQueues: boolean;
}

/**
 * A generated PowerShell script and the file name to save it under. The administrator runs it under
 * their own `az login` — the API executes nothing and holds no Azure permissions of its own.
 */
export interface TenantProvisioningScript {
  fileName: string;
  script: string;
}

/** Identifiers a Flowis account is registered under. */
export interface FlowisAccountIdentifiers {
  registrationNumber: string | null;
  taxId: string | null;
  vatId: string | null;
  participantId: string | null;
  countryCode: string | null;
}

/**
 * Registers an account Flowis already holds. It is read from Flowis as it is registered — the
 * identifiers come from there rather than from here, and an id Flowis does not hold is refused.
 * `clientId`/`clientSecret` optionally hand the account its own API client pair: the read
 * authenticates with it, and it is stored (secret protected) as what the account sends with. Left
 * out, the account uses the global pair.
 */
export interface CreateFlowisAccountRequest {
  accountId: string;
  name?: string | null;
  enabled: boolean;
  clientId?: string | null;
  clientSecret?: string | null;
}

/** Registers an account on its own; without `tenantGid` it waits in the unassigned pool. */
export interface CreateAccountRequest extends CreateFlowisAccountRequest {
  tenantGid: string | null;
}

/** Applied wholesale — an identifier left out is cleared, not kept. */
export interface UpdateFlowisAccountRequest extends FlowisAccountIdentifiers {
  name: string;
  enabled: boolean;
}

/**
 * Creates the account at the gate and registers the answer here in one step. `clientId`/`clientSecret`
 * optionally create it under the account's own API client pair, which is then stored as what it
 * sends with; left out, the global pair is used.
 */
export interface CreateRemoteAccountRequest {
  name: string;
  taxId: string;
  countryCode: string;
  registrationNumber?: string | null;
  vatId?: string | null;
  participantId?: string | null;
  documentMode?: string | null;
  tenantGid?: string | null;
  clientId?: string | null;
  clientSecret?: string | null;
}

/**
 * Hands an account its own Flowis API client pair, replacing any it already holds. The pair is
 * proven against the gate before it is stored; the secret is protected at rest and never returned.
 */
export interface SetAccountCredentialRequest {
  clientId: string;
  clientSecret: string;
}
